/** Storage page — package-grouped view with artifact detail drawer (GH-256) */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Upload, Search, LayoutGrid, List, ArrowLeft, Download, ChevronDown, ChevronRight, Package } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../lib/api-client'
import { StorageDropZone } from '../components/storage/storage-drop-zone'
import { cn } from '../lib/utils'
import { useAppStore } from '../stores/app-store'
import { useStorageFolderTree, useStorageFiles, useUploadToFolder } from '../hooks/use-storage'
import { useDeleteUpload } from '../hooks/use-uploads'
import { StorageSidebar } from '../components/storage/storage-sidebar'
import { StorageBreadcrumb } from '../components/storage/storage-breadcrumb'
import { StorageFileGrid } from '../components/storage/storage-file-grid'
import { DuplicatesBanner } from '../components/storage/duplicates-banner'
import { UploadProgressList } from '../components/storage/upload-progress-list'
import { ConfirmDialog } from '../components/ui/confirm-dialog'
import { StoragePreviewModal } from '../components/storage/storage-preview-modal'
import { ArtifactDrawer } from '../features/artifact/artifact-drawer'
import type { StorageFolderNode, StorageFile } from '../hooks/use-storage'

const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB

/** Toolbar filter for package-grouped view */
type PackageFilter = 'all' | 'assigned' | 'unassigned'

/** Get direct children of activeFolderId from the flat tree */
function getSubfolders(tree: StorageFolderNode[], activeFolderId: string | null): StorageFolderNode[] {
  if (!activeFolderId) return tree
  const map = new Map<string, StorageFolderNode>()
  function flatten(nodes: StorageFolderNode[]) {
    for (const n of nodes) { map.set(n.id, n); flatten(n.children) }
  }
  flatten(tree)
  return map.get(activeFolderId)?.children ?? []
}

/** Group files by packageId; null packageId → Unassigned bucket */
function groupFilesByPackage(files: StorageFile[]): Map<string | null, { packageName: string | null; files: StorageFile[] }> {
  const groups = new Map<string | null, { packageName: string | null; files: StorageFile[] }>()
  for (const file of files) {
    const key = file.packageId ?? null
    if (!groups.has(key)) {
      groups.set(key, { packageName: file.packageName ?? null, files: [] })
    }
    groups.get(key)!.files.push(file)
  }
  return groups
}

/** Apply package-assignment filter to a flat file list */
function applyPackageFilter(files: StorageFile[], filter: PackageFilter): StorageFile[] {
  if (filter === 'assigned') return files.filter((f) => f.packageId != null)
  if (filter === 'unassigned') return files.filter((f) => f.packageId == null)
  return files
}

export function StoragePage() {
  const { theme } = useAppStore()
  const isDark = theme === 'dark'
  const navigate = useNavigate()
  const { t } = useTranslation('artifact')
  const { folderId: routeFolderId } = useParams<{ folderId?: string }>()

  const [activeFolderId, setActiveFolderId] = useState<string | null>(routeFolderId ?? null)
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'list' : 'grid',
  )
  const [packageFilter, setPackageFilter] = useState<PackageFilter>('all')
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string | null>>(new Set())

  // Artifact drawer state
  const [drawerArtifactId, setDrawerArtifactId] = useState<string | null>(null)
  const [drawerSourceFile, setDrawerSourceFile] = useState<StorageFile | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [alertMsg, setAlertMsg] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; filename: string } | null>(null)
  const [previewFile, setPreviewFile] = useState<StorageFile | null>(null)

  const { data: treeData } = useStorageFolderTree()
  const {
    data: filesData,
    isLoading: filesLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useStorageFiles(activeFolderId)
  const uploadToFolder = useUploadToFolder()
  const deleteUpload = useDeleteUpload()

  const folders = treeData?.folders ?? []
  const allFiles = useMemo(
    () => filesData?.pages.flatMap((p) => p.files) ?? [],
    [filesData],
  )

  // Sync route → state
  useEffect(() => {
    setActiveFolderId(routeFolderId ?? null)
  }, [routeFolderId])

  const handleSelectFolder = useCallback((id: string | null) => {
    setActiveFolderId(id)
    setSearch('')
    if (id) {
      navigate(`/storage/${id}`, { replace: true })
    } else {
      navigate('/storage', { replace: true })
    }
  }, [navigate])

  const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList?.length) return
    const oversized: string[] = []
    const validFiles = Array.from(fileList).filter((f) => {
      if (f.size > MAX_FILE_SIZE) { oversized.push(f.name); return false }
      return true
    })
    if (oversized.length) setAlertMsg(`${oversized.map((n) => `"${n}"`).join(', ')} ${oversized.length === 1 ? 'is' : 'are'} too large (max 100MB)`)
    await Promise.allSettled(validFiles.map((file) => uploadToFolder(file, activeFolderId)))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [uploadToFolder, activeFolderId])

  const handleFileDelete = useCallback((id: string, filename: string) => {
    setDeleteTarget({ id, filename })
  }, [])

  const confirmFileDelete = useCallback(async () => {
    if (!deleteTarget) return
    await deleteUpload.mutateAsync(deleteTarget.id)
    setDeleteTarget(null)
  }, [deleteTarget, deleteUpload])

  // Open artifact drawer when a file row is clicked
  const handleFileClick = useCallback((file: StorageFile) => {
    setDrawerArtifactId(file.id)
    setDrawerSourceFile(file)
  }, [])

  const handleDrawerClose = useCallback(() => {
    setDrawerArtifactId(null)
    setDrawerSourceFile(null)
  }, [])

  const [exportBusy, setExportBusy] = useState(false)
  const handleExportMarkdown = useCallback(async () => {
    if (exportBusy) return
    setExportBusy(true)
    try {
      const ids = (allFiles ?? []).map((f) => f.id)
      if (ids.length === 0) { setAlertMsg('Nothing to export.'); return }
      const res = await apiClient.get<{ items: Array<{ filename: string; markdown: string }> }>(
        `/api/ingest/markdown-bundle?uploadIds=${ids.join(',')}`,
      )
      if (res.items.length === 0) { setAlertMsg('None of the visible files have extracted markdown yet.'); return }
      const { default: JSZip } = await import('jszip')
      const zip = new JSZip()
      const seen = new Map<string, number>()
      for (const it of res.items) {
        const idx = (seen.get(it.filename) ?? 0) + 1
        seen.set(it.filename, idx)
        const name = idx === 1 ? it.filename : it.filename.replace(/\.md$/, `-${idx}.md`)
        zip.file(name, it.markdown)
      }
      const blob = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `agentwiki-storage-${res.items.length}items.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      setAlertMsg(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setExportBusy(false)
    }
  }, [allFiles, exportBusy])

  // Search filter (client-side)
  const searchFiltered = search
    ? allFiles.filter((f) => f.filename.toLowerCase().includes(search.toLowerCase()))
    : allFiles

  // Package-assignment filter
  const packageFiltered = applyPackageFilter(searchFiltered, packageFilter)

  // Group by package (only when not searching into a folder view)
  const packageGroups = useMemo(() => groupFilesByPackage(packageFiltered), [packageFiltered])

  // Subfolders for folder view (hide when searching)
  const subfolders = search ? [] : getSubfolders(folders, activeFolderId)

  const toggleGroup = useCallback((key: string | null) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      const mapKey = key ?? '__unassigned__'
      if (next.has(mapKey as string | null)) {
        next.delete(mapKey as string | null)
      } else {
        next.add(mapKey as string | null)
      }
      return next
    })
  }, [])

  const isGroupCollapsed = (key: string | null) => collapsedGroups.has((key ?? '__unassigned__') as string | null)

  // Show package-grouped layout when files have any package data or filter is active
  const showGrouped = packageGroups.size > 1 || packageFilter !== 'all' || allFiles.some((f) => f.packageId != null)

  return (
    <div className={cn('flex h-full flex-col', isDark ? 'bg-surface-0' : 'bg-neutral-50')}>
      {/* Top bar */}
      <div className={cn('flex items-center gap-3 border-b px-4 py-3', isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white')}>
        <button
          onClick={() => navigate(-1)}
          className={cn('rounded-md p-1', isDark ? 'text-neutral-500 hover:bg-surface-3 hover:text-neutral-300' : 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700')}
          title="Go back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <StorageBreadcrumb
          folders={folders}
          activeFolderId={activeFolderId}
          onSelect={handleSelectFolder}
          isDark={isDark}
        />
        <div className="flex-1" />

        {/* Package filter — All / Assigned / Unassigned */}
        <div className={cn('hidden sm:flex rounded-lg border text-xs', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}>
          {(['all', 'assigned', 'unassigned'] as PackageFilter[]).map((f, i, arr) => (
            <button
              key={f}
              onClick={() => setPackageFilter(f)}
              data-testid={`package-filter-${f}`}
              className={cn(
                'px-2.5 py-1.5 transition-colors',
                i === 0 ? 'rounded-l-lg' : i === arr.length - 1 ? 'rounded-r-lg' : '',
                packageFilter === f
                  ? isDark ? 'bg-surface-3 text-neutral-200' : 'bg-neutral-100 text-neutral-700'
                  : isDark ? 'text-neutral-500 hover:text-neutral-300' : 'text-neutral-400 hover:text-neutral-600',
              )}
            >
              {t(`storage.filter.${f}`)}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative hidden sm:block">
          <Search className={cn('absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2', isDark ? 'text-neutral-500' : 'text-neutral-400')} />
          <input
            type="text"
            placeholder="Filter files..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cn(
              'w-44 rounded-lg border py-1.5 pl-8 pr-3 text-xs outline-none',
              isDark
                ? 'border-white/[0.06] bg-surface-2 text-neutral-200 placeholder-neutral-500 focus:border-brand-500/50'
                : 'border-neutral-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400 focus:border-brand-500',
            )}
          />
        </div>

        {/* View toggle */}
        <div className={cn('flex rounded-lg border', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}>
          {(['grid', 'list'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={cn(
                'p-1.5',
                mode === 'grid' ? 'rounded-l-lg' : 'rounded-r-lg',
                viewMode === mode
                  ? isDark ? 'bg-surface-3 text-neutral-200' : 'bg-neutral-100 text-neutral-700'
                  : isDark ? 'text-neutral-500 hover:text-neutral-300' : 'text-neutral-400 hover:text-neutral-600',
              )}
              title={mode === 'grid' ? 'Grid view' : 'List view'}
            >
              {mode === 'grid' ? <LayoutGrid className="h-3.5 w-3.5" /> : <List className="h-3.5 w-3.5" />}
            </button>
          ))}
        </div>

        {/* Export markdown */}
        <button
          onClick={handleExportMarkdown}
          disabled={exportBusy}
          title="Download all extracted markdown for the visible files as a ZIP"
          className={cn(
            'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50',
            isDark
              ? 'border-white/[0.08] text-neutral-300 hover:bg-surface-3'
              : 'border-neutral-200 text-neutral-700 hover:bg-neutral-100',
          )}
        >
          <Download className="h-3.5 w-3.5" />
          {exportBusy ? 'Bundling…' : 'Export markdown'}
        </button>

        {/* Upload */}
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-500"
        >
          <Upload className="h-3.5 w-3.5" />
          Upload
        </button>
        <input ref={fileInputRef} type="file" multiple onChange={handleUpload} className="hidden" />
      </div>

      <div className={cn('border-b px-4 py-2 text-xs', isDark ? 'border-white/[0.06] bg-surface-1 text-neutral-400' : 'border-neutral-200 bg-white text-neutral-600')}>
        Storage holds raw uploads and extracted files. Reviewed content becomes wiki knowledge after you commit it from ingest.
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — hidden on mobile */}
        <div className="hidden md:flex">
          <StorageSidebar activeFolderId={activeFolderId} onSelect={handleSelectFolder} isDark={isDark} />
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <UploadProgressList isDark={isDark} />
          <DuplicatesBanner files={packageFiltered} isDark={isDark} />

          <div
            data-tour="storage-grouped"
            role="region"
            aria-label="Storage content"
            className="flex-1 overflow-y-auto p-4 space-y-6"
          >
            {/* Package-grouped view */}
            {showGrouped && !filesLoading ? (
              <div className="space-y-6">
                {[...packageGroups.entries()].map(([pkgId, group]) => {
                  const isUnassigned = pkgId === null
                  const groupKey = pkgId ?? '__unassigned__'
                  const collapsed = isGroupCollapsed(pkgId)

                  return (
                    <div key={groupKey} className="space-y-2" data-testid={`package-group-${groupKey}`}>
                      {/* Group header */}
                      <button
                        onClick={() => toggleGroup(pkgId)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-medium transition-colors',
                          isDark
                            ? 'bg-surface-2 text-neutral-300 hover:bg-surface-3'
                            : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                        )}
                        aria-expanded={!collapsed}
                        data-testid={`package-group-toggle-${groupKey}`}
                      >
                        {collapsed
                          ? <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                          : <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                        }
                        {isUnassigned ? (
                          <span className={cn(isDark ? 'text-neutral-400' : 'text-neutral-500')}>
                            {t('storage.group.unassigned')}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5">
                            <Package className="h-3 w-3 text-brand-400" />
                            {group.packageName}
                          </span>
                        )}
                        <span className={cn('ml-auto text-[10px]', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
                          {group.files.length}
                        </span>
                      </button>

                      {/* Group files */}
                      {!collapsed && (
                        <StorageFileGrid
                          subfolders={pkgId === null ? subfolders : []}
                          files={group.files}
                          viewMode={viewMode}
                          isDark={isDark}
                          onFolderOpen={handleSelectFolder}
                          onFileDelete={handleFileDelete}
                          onFilePreview={(file) => {
                            // Row click → open artifact drawer instead of media preview
                            handleFileClick(file)
                          }}
                          isLoading={false}
                          filterQuery={search}
                          onClearFilter={() => setSearch('')}
                        />
                      )}
                    </div>
                  )
                })}

                {/* Load more */}
                {hasNextPage && !search && (
                  <div className="flex justify-center py-4">
                    <button
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className={cn(
                        'rounded-lg px-4 py-2 text-xs font-medium',
                        isDark ? 'bg-surface-3 text-neutral-300 hover:bg-surface-4' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200',
                        isFetchingNextPage && 'opacity-50',
                      )}
                    >
                      {isFetchingNextPage ? 'Loading...' : 'Load more'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Flat view (legacy — no package data or still loading) */
              <>
                <StorageFileGrid
                  subfolders={subfolders}
                  files={packageFiltered}
                  viewMode={viewMode}
                  isDark={isDark}
                  onFolderOpen={handleSelectFolder}
                  onFileDelete={handleFileDelete}
                  onFilePreview={handleFileClick}
                  isLoading={filesLoading}
                  filterQuery={search}
                  onClearFilter={() => setSearch('')}
                />
                {hasNextPage && !search && (
                  <div className="flex justify-center py-4">
                    <button
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className={cn(
                        'rounded-lg px-4 py-2 text-xs font-medium',
                        isDark ? 'bg-surface-3 text-neutral-300 hover:bg-surface-4' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200',
                        isFetchingNextPage && 'opacity-50',
                      )}
                    >
                      {isFetchingNextPage ? 'Loading...' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <ConfirmDialog
        open={!!alertMsg}
        onClose={() => setAlertMsg(null)}
        title="Upload error"
        description={alertMsg ?? ''}
        variant="info"
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmFileDelete}
        title="Delete file"
        description={`Are you sure you want to delete "${deleteTarget?.filename}"?`}
        confirmLabel="Delete"
        variant="danger"
      />

      {/* Media preview lightbox (kept for backward compat; drawer is primary UX) */}
      <StoragePreviewModal
        file={previewFile}
        files={packageFiltered}
        onClose={() => setPreviewFile(null)}
        onNavigate={setPreviewFile}
      />

      {/* Artifact detail drawer */}
      <ArtifactDrawer
        artifactId={drawerArtifactId}
        sourceFile={drawerSourceFile}
        onClose={handleDrawerClose}
        isDark={isDark}
      />

      <StorageDropZone activeFolderId={activeFolderId} isDark={isDark} />
    </div>
  )
}
